import { LoaderFunctionArgs, MetaFunction } from '@remix-run/node'
import { typedjson, useTypedLoaderData } from 'remix-typedjson'

import { Breadcrumb, Container } from '~/components'
import { NO_IMAGE_URL } from '~/constants'
import { PrefectureHeader, TopFooter } from '~/features'
import { PlanDetailPage } from '~/features/plan-detail-page'
import { searchPrefecture } from '~/models/master.server'
import { findOfficeById } from '~/models/office.server'
import { findPlanById } from '~/models/plan.server'
import { Image } from '~/models/types'
import { encodeImageUrl } from '~/utils/encode-image-url'

export const loader = async ({ params }: LoaderFunctionArgs) => {
  const { prefectureSlug, officeId, planId } = params
  if (!prefectureSlug || !officeId || !planId)
    throw new Response('Bad Request', { status: 400 })

  const [prefecture] = await searchPrefecture({ slug: prefectureSlug })
  if (!prefecture)
    throw new Response('Not Found', { status: 404, statusText: 'Not Found.' })

  const plan = await findPlanById(Number(planId))
  if (!plan)
    throw new Response('Not Found', { status: 404, statusText: 'Not Found.' })

  const office = await findOfficeById(Number(officeId))
  if (!office) throw new Response('Bad Request', { status: 400 })

  return typedjson({ prefecture, plan })
}

export const meta: MetaFunction<typeof loader> = ({ data, matches }) => {
  const planName = data?.plan.name || ''
  const planDescription = data?.plan.description || ''
  const sortedImages = data?.plan.images.sort(
    (a: Image, b: Image) => a.priority - b.priority,
  )
  const firstImage =
    sortedImages.find((image: Image) => image.isEyeCatch) || sortedImages[0]
  const titlePrefix = planName ? `${planName} | プラン詳細` : 'プラン詳細'
  const parentMeta = matches
    .flatMap((match) => match.meta ?? [])
    .filter((meta) => !('title' in meta))
  return [
    ...parentMeta,
    { title: titlePrefix + ' | シェアオフィス コワーキングスペース検索サイト' },
    { property: 'og:title', content: planName },
    { property: 'og:description', content: planDescription },
    {
      property: 'og:image',
      content: firstImage?.url ? encodeImageUrl(firstImage.url) : NO_IMAGE_URL,
    },
  ]
}

export default function Index() {
  const { prefecture, plan } = useTypedLoaderData<typeof loader>()

  return (
    <>
      <PrefectureHeader prefecture={prefecture} />
      <Container>
        <Breadcrumb
          links={[
            { text: 'ホーム', href: '/' },
            { text: prefecture.name, href: `/prefectures/${prefecture.slug}` },
            {
              text: plan.office.name,
              href: `/prefectures/${prefecture.slug}/offices/${plan.office.id}`,
            },
            { text: plan.name },
          ]}
        />
      </Container>
      <PlanDetailPage plan={plan} />
      <TopFooter />
    </>
  )
}
